import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Phone,
  LayoutDashboard,
  PhoneCall,
  MessageSquare,
  ClipboardList,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  UsersRound,
  CheckCircle,
  BarChart3,
  Sparkles,
  Bell,
} from "lucide-react";
import NotificationsPopover from "@/components/NotificationsPopover";

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isAgent = user?.role === "agent";

  const navItems = isAgent ? [
    { to: "/dashboard", icon: LayoutDashboard, label: "Mi Dashboard" },
    { to: "/calls", icon: PhoneCall, label: "Mis Llamadas" },
    { to: "/chats", icon: MessageSquare, label: "Mis Chats" },
    { to: "/alerts", icon: Bell, label: "Mis Alertas" },
  ] : [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard General" },
    { to: "/calls", icon: PhoneCall, label: "Llamadas" },
    { to: "/chats", icon: MessageSquare, label: "Mensajes WhatsApp" },
    { to: "/alerts", icon: Bell, label: "Alertas de Riesgo" },
    { to: "/teams", icon: UsersRound, label: "Equipos" },
    { to: "/matrices", icon: ClipboardList, label: "Matrices" },
  ];

  const dashboardMetrics = isAgent ? [] : [
    { to: "/dashboard-builder/200", icon: ClipboardList, label: "Facturas" },
    { to: "/dashboard-builder/201", icon: ClipboardList, label: "Front Desk" },
    { to: "/dashboard-builder/199", icon: ClipboardList, label: "Trámites Aduanales" },
    { to: "/dashboard-builder/166", icon: ClipboardList, label: "Matriz de Calidad" },
    { to: "/dashboard-builder/1", icon: ClipboardList, label: "Servicio al Cliente" },
  ];

  const aiInsightsItems = isAgent ? [] : [
    { to: "/auto-insights", icon: Sparkles, label: "Auto Insights" },
  ];

  const qaItems = isAgent ? [] : [
    { to: "/qa/dashboard", icon: BarChart3, label: "Dashboard QA" },
  ];

  const adminItems = isAgent ? [] : [
    { to: "/users", icon: Users, label: "Usuarios" },
    { to: "/config", icon: Settings, label: "Configuración" },
  ];

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `sidebar-item ${isActive ? "active" : "text-slate-600 hover:text-slate-900"}`
      }
      data-testid={`nav-${label.toLowerCase()}`}
    >
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-sky-500 to-sky-600 rounded-lg flex items-center justify-center shadow-md shadow-sky-500/20">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <span className="pioqa-logo text-2xl text-slate-900">PIOQA</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <div className="mb-4">
              <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Principal
              </p>
              {navItems.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </div>

            <div className="pt-4 border-t border-slate-100 mb-4">
              <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Métricas de Llamadas
              </p>
              {dashboardMetrics.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </div>

            <div className="pt-4 border-t border-slate-100 mb-4">
              <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Auto Insights IA
              </p>
              {aiInsightsItems.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </div>

            <div className="pt-4 border-t border-slate-100 mb-4">
              <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Control de Calidad
              </p>
              {qaItems.map((item) => (
                <NavItem key={item.to} {...item} />
              ))}
            </div>

            {user?.role === "admin" && (
              <div className="pt-4 border-t border-slate-100">
                <p className="px-4 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  Administración
                </p>
                {adminItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </div>
            )}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
              <Avatar className="h-10 w-10 bg-sky-100">
                <AvatarFallback className="bg-sky-100 text-sky-600 font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <NotificationsPopover />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                    <Avatar className="h-8 w-8 bg-sky-100">
                      <AvatarFallback className="bg-sky-100 text-sky-600 text-sm font-semibold">
                        {user?.name?.charAt(0)?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronRight className="w-4 h-4 text-slate-400 rotate-90" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
